/**
 * Geometry for donut slices. Kept free of runtime imports so the maths can be
 * exercised on its own.
 *
 * Angles run clockwise from 12 o'clock, matching the design.
 */

/** Gutter between neighbouring slices, measured along the mid-radius arc. */
export const SLICE_GAP = 2;
/** Slice corner rounding, as a share of the ring thickness. */
export const CORNER_RADIUS_RATIO = 0.3;

export const pointOnCircle = (
    center: number,
    radius: number,
    angle: number,
): { x: number; y: number } => ({
    x: center + Math.sin(angle) * radius,
    y: center - Math.cos(angle) * radius,
});

const clampCornerRadius = (
    outerRadius: number,
    innerRadius: number,
    sweep: number,
): number => {
    const desired = (outerRadius - innerRadius) * CORNER_RADIUS_RATIO;
    // Each edge carries two corners, so neither may overrun the arc between
    // them. The margin keeps that arc from inverting on very thin slices,
    // which would otherwise sweep the long way around the circle.
    const halfSweep = Math.min(sweep / 2, Math.PI / 2) * 0.96;
    const sinHalfSweep = Math.sin(halfSweep);

    return Math.max(
        0,
        Math.min(
            desired,
            // Opposite corners must not meet inside the ring.
            (outerRadius - innerRadius) / 2 - 0.5,
            (innerRadius * sinHalfSweep) / (1 - sinHalfSweep),
            (outerRadius * sinHalfSweep) / (1 + sinHalfSweep),
        ),
    );
};

export const buildDonutSlicePath = ({
    center,
    outerRadius,
    innerRadius,
    startAngle,
    endAngle,
}: {
    center: number;
    outerRadius: number;
    innerRadius: number;
    startAngle: number;
    endAngle: number;
}): string => {
    const sweep = endAngle - startAngle;
    const cornerRadius = clampCornerRadius(outerRadius, innerRadius, sweep);
    const format = (value: number) => value.toFixed(2);
    const at = (radius: number, angle: number) => {
        const { x, y } = pointOnCircle(center, radius, angle);
        return `${format(x)},${format(y)}`;
    };

    if (cornerRadius < 0.5) {
        const large = sweep > Math.PI ? 1 : 0;

        return [
            `M${at(outerRadius, startAngle)}`,
            `A${format(outerRadius)},${format(outerRadius)} 0 ${large.toString()} 1 ${at(outerRadius, endAngle)}`,
            `L${at(innerRadius, endAngle)}`,
            `A${format(innerRadius)},${format(innerRadius)} 0 ${large.toString()} 0 ${at(innerRadius, startAngle)}`,
            'Z',
        ].join(' ');
    }

    // Each corner is a circle of radius `cornerRadius` tangent to both a ring
    // edge and a radial edge, so its tangent points sit inset from the slice
    // boundary by these amounts.
    const outerInset = Math.asin(cornerRadius / (outerRadius - cornerRadius));
    const innerInset = Math.asin(cornerRadius / (innerRadius + cornerRadius));
    const outerCornerRadius = Math.sqrt(
        outerRadius ** 2 - 2 * outerRadius * cornerRadius,
    );
    const innerCornerRadius = Math.sqrt(
        innerRadius ** 2 + 2 * innerRadius * cornerRadius,
    );
    const outerLarge = sweep - 2 * outerInset > Math.PI ? 1 : 0;
    const innerLarge = sweep - 2 * innerInset > Math.PI ? 1 : 0;
    const corner = `A${format(cornerRadius)},${format(cornerRadius)} 0 0 1`;

    return [
        `M${at(outerRadius, startAngle + outerInset)}`,
        `A${format(outerRadius)},${format(outerRadius)} 0 ${outerLarge.toString()} 1 ${at(outerRadius, endAngle - outerInset)}`,
        `${corner} ${at(outerCornerRadius, endAngle)}`,
        `L${at(innerCornerRadius, endAngle)}`,
        `${corner} ${at(innerRadius, endAngle - innerInset)}`,
        `A${format(innerRadius)},${format(innerRadius)} 0 ${innerLarge.toString()} 0 ${at(innerRadius, startAngle + innerInset)}`,
        `${corner} ${at(innerCornerRadius, startAngle)}`,
        `L${at(outerCornerRadius, startAngle)}`,
        `${corner} ${at(outerRadius, startAngle + outerInset)}`,
        'Z',
    ].join(' ');
};
